import React, { useState } from "react";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import { crearCliente, TIPOS_CLIENTE, nombreCliente } from "../../utils/clientes";
import { parecidosAlAlta } from "../../utils/clientesBackfill";
import { XMarkIcon, UserIcon } from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";
const inputClass =
  "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

export const FORM_CLIENTE_VACIO = {
  clientType: "fisica",
  nombreEmpresa: "",
  nombre: "",
  apellido: "",
  correo: "",
  telefono: "",
  rfc: "",
  notas: "",
};

// Campos de la ficha, compartidos por el alta y la edición.
// Aviso de posibles duplicados al dar de alta: lista a quién se parece y
// por qué. No impide crear. Con `onUsar`, cada uno trae su botón para usar
// el que ya existe en lugar de crear otro.
export function AvisoDuplicados({ duplicados = [], onUsar }) {
  if (duplicados.length === 0) return null;
  return (
    <div className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
      <p className="font-medium">
        {duplicados.length === 1
          ? "Ya existe un cliente parecido. Si es la misma persona, úsalo en lugar de crear otro."
          : `Ya existen ${duplicados.length} clientes parecidos. Si alguno es la misma persona, úsalo en lugar de crear otro.`}
      </p>
      <ul className="mt-1.5 space-y-1">
        {duplicados.map(({ cliente, motivos }) => (
          <li key={cliente.id} className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate">
              <strong>{nombreCliente(cliente)}</strong>
              <span className="text-amber-700/80 dark:text-amber-300/70"> · mismo {motivos.join(", ")}</span>
            </span>
            {onUsar && (
              <button
                type="button"
                onClick={() => onUsar(cliente)}
                className="shrink-0 font-semibold underline hover:no-underline"
              >
                Usar este
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ClienteFormFields({ form, setForm, autoFocus = false }) {
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1">Tipo</label>
        <div className="flex gap-2">
          {TIPOS_CLIENTE.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => set({ clientType: t.value })}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                form.clientType === t.value
                  ? "border-transparent text-white"
                  : "border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-800 hover:bg-zinc-50"
              }`}
              style={form.clientType === t.value ? { backgroundColor: ACCENT } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {form.clientType === "moral" && (
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Nombre de la empresa</label>
          <input type="text" value={form.nombreEmpresa} onChange={(e) => set({ nombreEmpresa: e.target.value })} className={inputClass} />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            {form.clientType === "moral" ? "Nombre del contacto" : "Nombre"}
          </label>
          <input type="text" value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} className={inputClass} autoFocus={autoFocus} />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Apellido</label>
          <input type="text" value={form.apellido} onChange={(e) => set({ apellido: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Correo</label>
          <input type="email" value={form.correo} onChange={(e) => set({ correo: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Teléfono</label>
          <input type="tel" value={form.telefono} onChange={(e) => set({ telefono: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">RFC (opcional)</label>
          <input type="text" value={form.rfc} onChange={(e) => set({ rfc: e.target.value })} className={inputClass} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1">Notas (opcional)</label>
        <input type="text" value={form.notas} onChange={(e) => set({ notas: e.target.value })} className={inputClass} />
      </div>
    </div>
  );
}

// Alta de cliente desde el Directorio. Si ya existe alguien con ese correo
// o teléfono, lo dice antes de duplicar.
export default function ClienteCreateDialog({ open, onClose, onCreated, inicial = null }) {
  const { selectedVenueId, clientes } = useVenue();
  const { userInfo, user } = useAuth();
  const [form, setForm] = useState({ ...FORM_CLIENTE_VACIO, ...(inicial || {}) });
  const [saving, setSaving] = useState(false);

  const cerrar = () => {
    setForm({ ...FORM_CLIENTE_VACIO, ...(inicial || {}) });
    onClose?.();
  };

  const duplicados = parecidosAlAlta(form, clientes);

  const handleCrear = async () => {
    setSaving(true);
    try {
      const id = await crearCliente({ venueId: selectedVenueId, datos: form, user, origen: "manual" });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.CLIENTE_CREATE,
          entity: "cliente",
          entityId: id,
          entityName: nombreCliente(form),
          details: {},
        },
        userInfo,
        user
      );
      showAlert("Cliente creado", "success");
      cerrar();
      onCreated?.(id, { id, ...form });
    } catch (err) {
      showAlert(err.message || "No se pudo crear el cliente", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={cerrar} />
      <div className="relative w-full max-w-lg rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: ACCENT + "1a" }}>
            <UserIcon className="h-4 w-4" style={{ color: ACCENT }} />
          </div>
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Nuevo cliente</h2>
          <button type="button" onClick={cerrar} className="ml-auto text-zinc-400 hover:text-zinc-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          <ClienteFormFields form={form} setForm={setForm} autoFocus />
          <AvisoDuplicados duplicados={duplicados} />
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
          <button type="button" onClick={cerrar} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCrear}
            disabled={saving}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
          >
            {saving ? "Creando…" : "Crear cliente"}
          </button>
        </div>
      </div>
    </div>
  );
}
